import { useQuery, useMutation, QueryClient, useQueryClient } from '@tanstack/react-query';
import accountingAPI from '@global-apis/accounting';
import { gql, TypedDocumentNode } from '@apollo/client';
import { graphqlClient } from '@global-apis/config';
import { staleTimes, queryKeys } from '@root/constants/queryKeys';

interface CostCode {
  id: number;
  code: string;
  description: string;
  label: string;
  account: string;
  parent_id?: number;
  qbo_account: string;
  income_account: string;
  external_data?: {
    qbd_code?: string;
    qbo?: {
      line_items_type?: string;
    };
    qbd?: {
      line_items_type?: string;
    };
  };
  external_reference?: string;
}

interface CostCodeWithoutParentData {
  costCodes: {
    results: CostCode[];
  };
}

interface CostCodeVariables {
  filters: {
    is_leaf_nodes: boolean;
  };
}

export const GET_COST_CODES_QUERY: TypedDocumentNode<CostCodeWithoutParentData, CostCodeVariables> = gql`
  query GetCostCodesWithoutParent($filters: CompaniesCostCodesFilters, $limit: Int, $sort_by: SortBy) {
    costCodes(filters: $filters, limit: $limit, sort_by: $sort_by) {
      results {
        id
        code
        description
        account
        parent_id
        qbo_account
        income_account
        external_data {
          qbd_code
          qbo {
            line_items_type
          }
          qbd {
            line_items_type
          }
        }
      }
    }
  }
`;

export const useCostCodeCreate = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CostCode & { company_id: number }>) => accountingAPI.costCode.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.costCodesWithParent });
      queryClient.invalidateQueries({ queryKey: queryKeys.costCodesWithoutParent });
    },
    ...options
  });
};

export const useCostCodeUpdate = (id: number, options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CostCode>) => accountingAPI.costCode.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.costCodesWithParent });
      queryClient.invalidateQueries({ queryKey: queryKeys.costCodesWithoutParent });
    },
    ...options
  });
};

// it will return only cost codes being not parent. Used on cost code searcher/selector
export const useCostCodesWithoutParent = (options = {}) =>
  useQuery<CostCode[], Error>({
    queryKey: queryKeys.costCodesWithoutParent,
    staleTime: staleTimes.masterData,
    queryFn: async () => {
      try {
        const { data } = await graphqlClient.query({
          query: GET_COST_CODES_QUERY,
          fetchPolicy: 'network-only',
          variables: { filters: { is_leaf_nodes: true } }
        });

        return data.costCodes.results.map((costCode) => ({
          ...costCode,
          label: `${costCode.code} - ${costCode.description}`,
          external_reference: costCode.external_data?.qbd_code
        }));
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole:
        console.log('error when fetching cost codes without parent', err);
        throw err;
      }
    },
    ...options
  });

// it will return all cost codes being parent or not. Used on budget list
export const useCostCodesWithParent = (options = {}) =>
  useQuery<CostCode[], Error>({
    queryKey: queryKeys.costCodesWithParent,
    staleTime: staleTimes.masterData,
    queryFn: async () => {
      try {
        const { data } = await graphqlClient.query({
          query: GET_COST_CODES_QUERY,
          fetchPolicy: 'network-only',
          variables: { filters: { is_leaf_nodes: false } }
        });

        return data.costCodes.results.map((costCode) => ({
          ...costCode,
          label: `${costCode.code} - ${costCode.description}`,
          external_reference: costCode.external_data?.qbd_code
        }));
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole:
        console.log('error when fetching cost codes with parent', err);
        throw err;
      }
    },
    ...options
  });

export const invalidateCostCodesWithoutParent = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: queryKeys.costCodesWithoutParent });

export const invalidateCostCodesWithParent = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: queryKeys.costCodesWithParent });

export const useCostCodeBulkUpdate = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      ids: number[];
      action: 'set_parent' | 'set_expense';
      parent_id?: number | null;
      expense_code_only?: boolean;
    }) => accountingAPI.costCode.bulkUpdate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.costCodesWithParent });
      queryClient.invalidateQueries({ queryKey: queryKeys.costCodesWithoutParent });
    },
    ...options
  });
};

export const useCostCodePreviewImport = (options = {}) =>
  useMutation({
    mutationFn: (file: File) => accountingAPI.costCode.previewImport(file),
    ...options
  });

export const useCostCodeConfirmImport = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rows: import('@global-apis/accounting').CostCodeImportRow[]) =>
      accountingAPI.costCode.confirmImport(rows),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.costCodesWithParent });
      queryClient.invalidateQueries({ queryKey: queryKeys.costCodesWithoutParent });
    },
    ...options
  });
};
